import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { LeaveDialog } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  VALID_PASSWORD_REGEX,
  VALID_SHELL_COMMAND_OPTIONS_REGEX,
} from '@ipfabric/utils/constants';
import {
  snmpAuthenticationTypes,
  snmpVersions,
} from '@ipfabric/utils/settings/types/types';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import z from 'zod';
import type { Snmp } from './model/SnmpModel';

export interface SnmpFormFields extends Omit<Snmp, 'hostIp'> {
  hostIp: IPSubnetInputValueOrDraft;
}

interface SnmpFormProps {
  onSubmit: (data: Snmp) => void;
  initialValues: Snmp;
  loading?: boolean;
}

export const SnmpForm = ({
  onSubmit,
  loading,
  initialValues,
}: SnmpFormProps) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useCustomForm<SnmpFormFields>({
    defaultValues: {
      ...initialValues,
      hostIp: { ips: initialValues.hostIp },
    },
    resolver: zodResolver(getValidationSchema),
  });

  // allow to follow values changes even after the form is initialized
  const [version, enabled] = useWatch({
    name: ['version', 'enabled'],
    control,
  });

  // allows to re-initialize the form with new initialValues
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is a stable function
  useEffect(() => {
    reset({
      ...initialValues,
      hostIp: { ips: initialValues.hostIp },
    });
  }, [initialValues]);

  useEffect(() => {
    if (!enabled) {
      // Clear all errors when form is disabled
      reset(undefined, {
        keepValues: true,
        keepDirty: true,
        keepIsSubmitted: false,
        keepErrors: false,
      });
    }
  }, [enabled, reset]);

  return (
    <Card className='p flex flex-col gap'>
      <div className='text-level-5 font-semibold'>SNMP monitoring support</div>
      The IP Fabric appliance (CPU, memory, hard drives, and network interfaces)
      can be monitored using the SNMP protocol.
      <form
        onSubmit={handleSubmit((values) =>
          onSubmit({ ...values, hostIp: values.hostIp.ips }),
        )}
        className='max-w-[336px] flex flex-col gap'
      >
        <LeaveDialog dirty={isDirty} />
        <FormSwitch
          control={control}
          name='enabled'
          description='Enable monitoring support'
          disabled={loading}
        />
        <FormSelect.Mandatory
          control={control}
          name='version'
          label='Version'
          options={snmpVersions}
          disabled={!enabled || loading}
        />
        <FormIPSubnetField
          control={control}
          name='hostIp'
          label='NMS IPs'
          disabled={!enabled || loading}
          type='ip'
          labelTooltip='IP addresses of NMS server (or more servers) which collects SNMP information from IP Fabric image. These IPs will be allowed to access UDP/161.'
          placeholder='Insert one or more IPv4/IPv6 addresses'
        />
        <FormTextField
          control={control}
          name='locality'
          label='Locality'
          type='text'
          disabled={!enabled || loading}
        />
        <FormTextField
          control={control}
          name='systemContact'
          label='System contact'
          type='text'
          disabled={!enabled || loading}
        />
        {version === '2c' && (
          <FormTextField
            control={control}
            name='communityString'
            label='Community string'
            type='text'
            disabled={!enabled || loading}
          />
        )}
        {version === '3' && (
          <>
            <FormTextField
              control={control}
              name='username'
              label='Username'
              type='text'
              disabled={!enabled || loading}
            />
            <FormTextField
              control={control}
              name='passphrase'
              label='Passphrase'
              type='password'
              disabled={!enabled || loading}
            />
            <FormTextField
              control={control}
              name='encryptionPassphrase'
              label='Encryption passphrase'
              type='password'
              disabled={!enabled || loading}
            />
            <FormSelect.Mandatory
              control={control}
              name='authenticationType'
              label='Authentication type'
              options={snmpAuthenticationTypes}
              disabled={!enabled || loading}
            />
          </>
        )}

        <Button
          variant='primary'
          loading={loading}
          type='submit'
          disabled={!isDirty}
          icon='fa-save'
        >
          Save
        </Button>
      </form>
    </Card>
  );
};

const getValidationSchema = z
  .object({
    enabled: z.boolean(),
    version: z.string().optional(),
    hostIp: ipSubnetInputValueSchema.optional(),
    locality: z.string().optional(),
    systemContact: z.string().optional(),
    communityString: z.string().optional(),
    username: z.string().optional(),
    passphrase: z.string().optional(),
    encryptionPassphrase: z.string().optional(),
    authenticationType: z.string().optional(),
  })
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: complexity is acceptable for validation
  .superRefine((data, ctx) => {
    if (!data.enabled) {
      return; // If enabled is false, no need to validate the rest of the fields
    }

    if (data.hostIp !== undefined) {
      if (hasIPSubnetInputValueError(data.hostIp)) {
        // Display the error message from the draft value
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: data.hostIp.message,
          path: ['hostIp'],
        });
      } else if (data.hostIp.ips.length === 0) {
        // Validate that at least one subnet is provided
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'NMS IPs is required and must contain at least one element',
          path: ['hostIp'],
        });
      }
    }

    if (
      data.systemContact &&
      !VALID_SHELL_COMMAND_OPTIONS_REGEX.test(data.systemContact)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'System contact must contain only valid characters',
        path: ['systemContact'],
      });
    }

    if (data.version === '2c' && !data.communityString) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Community string is required',
        path: ['communityString'],
      });
    }

    if (
      data.communityString &&
      !VALID_SHELL_COMMAND_OPTIONS_REGEX.test(data.communityString)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Community string must contain only valid characters',
        path: ['communityString'],
      });
    }

    if (data.version === '3') {
      if (!data.username) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Username is required',
          path: ['username'],
        });
      }
      if (!data.passphrase) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Passphrase is required',
          path: ['passphrase'],
        });
      }
      if (!data.encryptionPassphrase) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Encryption passphrase is required',
          path: ['encryptionPassphrase'],
        });
      }
    }

    if (data.passphrase && data.passphrase.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 8,
        type: 'string',
        inclusive: true,
        message: 'Passphrase must be at least 8 characters long',
        path: ['passphrase'],
      });
    }

    if (data.encryptionPassphrase && data.encryptionPassphrase.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 8,
        type: 'string',
        inclusive: true,
        message: 'Encryption passphrase must be at least 8 characters long',
        path: ['encryptionPassphrase'],
      });
    }

    if (data.passphrase && !VALID_PASSWORD_REGEX.test(data.passphrase)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Passphrase must match the pattern: ${VALID_PASSWORD_REGEX}`,
        path: ['passphrase'],
      });
    }

    if (
      data.encryptionPassphrase &&
      !VALID_PASSWORD_REGEX.test(data.encryptionPassphrase)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Encryption passphrase must match the pattern: ${VALID_PASSWORD_REGEX}`,
        path: ['encryptionPassphrase'],
      });
    }
  });
